/**
 * TraceEvent[] → Beat[] — the semantic layer of the Run Theater.
 *
 * Beats say WHAT happened in stage language ("fetch books", "call an outside
 * service", "brief a specialist"); the Director decides HOW it is performed.
 * This module is pure: same events in, same beats out, no clocks, no state —
 * the Director dedupes re-ingested beats by (seq, kind, lane).
 *
 * All human-facing chip/caption text comes from a `BeatStrings` pack so the
 * theater speaks the viewer's language; callers pass the active locale's pack
 * (`t.beats`) and the English catalog is the default, keeping pure-module
 * tests and legacy call sites unchanged.
 *
 * Input is whatever the run views already have: the post-`groupFanouts` list.
 * Fanout wrappers are re-flattened here (their nested per-agent mini-traces
 * carry the original seq/ts), so the Director always sees one append-only
 * chronological stream regardless of how the adapter regrouped it.
 */

import type { SubagentRun, TraceEvent } from '@evt/flow-stream-ui'
import { beats as enBeats } from '../i18n/messages/en/beats'
import type { Beat } from './types'

export const MAIN_LANE = '__main__'

export interface BeatToolLabel {
  chip: string
  caption: string
}

/** Known first-party tools whose activity gets bespoke stage language. */
export type BeatToolName =
  | 'code_execution'
  | 'bash_code_execution'
  | 'text_editor_code_execution'
  | 'str_replace_editor'
  | 'web_search'
  | 'web_fetch'

/**
 * Every human-visible string the beat layer can produce. Chips float over a
 * character's head — they must read like what a coworker would say they're
 * doing, never like an API id. The English pack lives in the i18n catalog
 * (`messages/en/beats.ts`) and doubles as the default.
 */
export interface BeatStrings {
  gettingStarted: string
  reviewingBrief: string
  attachmentsChip: (n: number) => string
  collectingAttachments: (n: number) => string
  referenceVolumesChip: (n: number) => string
  consultingLibrary: (n: number) => string
  thinking: string
  writing: string
  coffeeBreak: string
  retryChip: (attempt: number) => string
  retryCaption: (attempt: number) => string
  /** The agent reached for a tool it does not have and worked around it (fr-docs ADR-0024). */
  adjustingChip: string
  adjustingCaption: string
  checkingNotesChip: string
  checkingNotesCaption: string
  noReplyInTime: string
  runCancelled: string
  replyArrived: string
  waitingForReply: string
  filesChip: (n: number) => string
  packingFiles: (n: number) => string
  delivered: string
  deliveredWithNotes: string
  runFailed: string
  runFailedWithReason: (reason: string) => string
  hasAQuestion: string
  briefingSpecialist: string
  callingChip: (server: string) => string
  onTheLine: (server: string) => string
  workbenchCaption: (task: string) => string
  toolLabels: Record<BeatToolName, BeatToolLabel>
  /** Director-level stage language (not tied to a single beat). */
  settingTheStage: string
  specialistsOnIt: (caption: string, n: number) => string
  /** Chip over the head of whoever is at the coffee bar. */
  breakChip: string
  /** The lead is at the door, holding it open for the arriving specialists. */
  welcomingSpecialists: string
  /** The standup on the rug where the brief is handed over. */
  standupBriefing: string
}

/** Tools that read as "talking to the outside world" (console, not bench). */
const CONSOLE_TOOLS = new Set(['web_search', 'web_fetch'])

function isKnownTool(name: string): name is BeatToolName {
  return name in enBeats.toolLabels
}

/** 'sap-abap_architect' → 'sap abap architect' — ids become words. */
function words(s: string): string {
  return s.replaceAll(/[-_]+/g, ' ').trim()
}

export function eventsToBeats(events: TraceEvent[], strings: BeatStrings = enBeats): Beat[] {
  const flat = flatten(events)
  const beats: Beat[] = []
  for (const e of flat) {
    const beat = toBeat(e, strings)
    if (beat) beats.push(beat)
  }
  beats.sort((a, b) => (a.seq === b.seq ? a.atMs - b.atMs : a.seq - b.seq))
  return beats
}

/** Restore fanout children into the flat stream; drop the synthetic wrapper. */
function flatten(events: TraceEvent[]): TraceEvent[] {
  const out: TraceEvent[] = []
  for (const e of events) {
    if (e.kind === 'subagent_fanout') {
      for (const agent of e.agents) out.push(...agent.events)
      continue
    }
    out.push(e)
  }
  return out
}

function laneOf(e: TraceEvent): string {
  if (e.kind === 'subagent_started' || e.kind === 'subagent_finished') return e.meta.agentId
  if (e.phase === 'subagent' && e.agentId) return e.agentId
  return MAIN_LANE
}

function toBeat(e: TraceEvent, m: BeatStrings): Beat | null {
  const lane = laneOf(e)
  const base = { lane, atMs: e.atMs, seq: e.seq }

  switch (e.kind) {
    case 'request_received':
      return { ...base, kind: 'enter', caption: m.gettingStarted, milestone: true }
    case 'config_resolved':
      return { ...base, kind: 'orient', caption: m.reviewingBrief }
    case 'attachments_uploaded':
      return {
        ...base,
        kind: 'collect_mail',
        count: e.meta.count,
        chip: m.attachmentsChip(e.meta.count),
        caption: m.collectingAttachments(e.meta.count)
      }
    case 'knowledge_resolved':
      return {
        ...base,
        kind: 'fetch_books',
        count: e.meta.count,
        chip: m.referenceVolumesChip(e.meta.count),
        caption: m.consultingLibrary(e.meta.count)
      }
    case 'turn_start':
      return lane === MAIN_LANE ? { ...base, kind: 'sit_desk' } : null
    case 'thinking':
      return { ...base, kind: 'think', caption: m.thinking }
    case 'text':
      return { ...base, kind: 'write', caption: m.writing }
    case 'block_start': {
      // LIVE-only marker: a content block just OPENED upstream. This is what
      // lets the stage act DURING a multi-minute thinking/text/tool block —
      // the matching finalized event (thinking/text/tool_use) lands at the
      // block's END and simply merges into the act this one started. Stored
      // traces don't carry these (wire-only), so replays behave as before.
      // `live: true` keeps journey counters from double-counting the block.
      if (e.meta.blockType === 'thinking') return { ...base, kind: 'think', caption: m.thinking, live: true }
      if (e.meta.blockType === 'text') return { ...base, kind: 'write', caption: m.writing, live: true }
      return { ...toolActivityBeat(e.meta.name ?? 'tool', e.meta.server, base, m), live: true }
    }
    case 'tool_use':
      return toolActivityBeat(e.meta.name, e.meta.server, base, m)
    case 'tool_result':
      return { ...base, kind: 'tool_done', ...(e.meta.isError ? { error: true } : {}) }
    case 'cache_breakpoint':
      return { ...base, kind: 'sparkle' }
    case 'pause_turn_resumed':
      return { ...base, kind: 'stretch', caption: m.coffeeBreak }
    case 'engine_retry_attempt':
      return {
        ...base,
        kind: 'stumble',
        chip: m.retryChip(e.meta.attempt),
        caption: m.retryCaption(e.meta.attempt),
        milestone: true
      }
    case 'unknown_tool_recovered':
      // fr-docs ADR-0024. The customer-visible story is identical to a transport
      // retry — the agent tripped and carried on — so it performs as the same
      // beat, which also earns the journey's "Hit a snag ×N" folding and the
      // RunStats retry counter for free. The chip/caption deliberately do NOT
      // name the tool: an internal tool id means nothing on this surface.
      return { ...base, kind: 'stumble', chip: m.adjustingChip, caption: m.adjustingCaption, milestone: true }
    case 'memory_op':
      return { ...base, kind: 'file', chip: m.checkingNotesChip, caption: m.checkingNotesCaption }
    case 'subagent_started':
      return {
        ...base,
        kind: 'specialist_start',
        chip: shortAgent(e.meta.agentId),
        milestone: true
      }
    case 'subagent_finished':
      return {
        ...base,
        kind: 'specialist_done',
        chip: shortAgent(e.meta.agentId),
        ...(e.meta.status === 'failure' ? { error: true } : {}),
        milestone: true
      }
    case 'user_reply': {
      const caption =
        e.meta.trigger === 'timeout' ? m.noReplyInTime : e.meta.trigger === 'cancel' ? m.runCancelled : m.replyArrived
      return { ...base, kind: 'user_note', caption, milestone: true }
    }
    case 'awaiting':
      return {
        ...base,
        kind: 'wait_user',
        key: `awaiting:${e.meta.suspensionId}:${e.meta.roundCount}`,
        caption: m.waitingForReply,
        milestone: true
      }
    case 'files_collected':
      return {
        ...base,
        kind: 'pack',
        count: e.meta.count,
        chip: m.filesChip(e.meta.count),
        caption: m.packingFiles(e.meta.count),
        milestone: true
      }
    case 'final': {
      const partial = e.meta.status === 'partial_success'
      return {
        ...base,
        kind: 'deliver',
        caption: partial ? m.deliveredWithNotes : m.delivered,
        ...(partial ? { partial: true } : {}),
        milestone: true
      }
    }
    case 'error': {
      const reason = e.meta.code ? words(e.meta.code) : undefined
      return {
        ...base,
        kind: 'collapse',
        ...(reason ? { chip: reason } : {}),
        caption: reason ? m.runFailedWithReason(reason) : m.runFailed,
        milestone: true,
        error: true
      }
    }
    // Operator-only (fr-docs ADR-0024): the payload is raw Anthropic file ids for
    // recovery tooling. They mean nothing to a customer and are not theirs to
    // see, so this is silent BY NAME rather than falling into `default` — a
    // future reader must not "helpfully" surface provider ids on this surface.
    case 'output_files_orphaned':
      return null
    // Boundary/ambient events that don't need a performance of their own.
    case 'turn_end':
    case 'heartbeat':
    case 'subagent_progress':
    case 'subagent_fanout':
      return null
    default:
      return null
  }
}

/** Shared routing for tool activity — used by both the live `block_start`
 *  marker (block opened) and the finalized `tool_use` event (input complete).
 *  Both produce the SAME beat shape so the Director merges them into one act. */
function toolActivityBeat(
  name: string,
  serverName: string | undefined,
  base: Omit<Beat, 'kind'>,
  m: BeatStrings
): Beat {
  if (name === 'request_human_input') {
    return { ...base, kind: 'wait_user', caption: m.hasAQuestion, milestone: true }
  }
  if (name === 'delegate_to_subagent') {
    return { ...base, kind: 'brief_specialist', caption: m.briefingSpecialist, milestone: true }
  }
  if (serverName) {
    const server = words(serverName)
    return {
      ...base,
      kind: 'call',
      chip: trunc(m.callingChip(server), 22),
      caption: m.onTheLine(server)
    }
  }
  if (isKnownTool(name)) {
    const label = m.toolLabels[name]
    return { ...base, kind: CONSOLE_TOOLS.has(name) ? 'call' : 'tinker', chip: label.chip, caption: label.caption }
  }
  // Everything else — skills, unknown client tools — is bench work; the id
  // becomes words so the chip never reads like an API name.
  const pretty = words(name)
  return {
    ...base,
    kind: 'tinker',
    chip: trunc(pretty, 20),
    caption: m.workbenchCaption(pretty)
  }
}

/** Agent ids become readable words for chips ('sap-abap-architect' → 'sap abap architect'). */
export function shortAgent(agentId: string): string {
  return trunc(words(agentId), 20)
}

function trunc(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`
}

/**
 * Stable identity for Director dedupe across re-ingested arrays. Includes
 * `atMs` so two DIFFERENT runs (both starting at seq 1 with `enter`) never
 * collide — that's what lets RunTheater detect a stream switch and reset.
 * Locale is deliberately NOT part of identity: switching language mid-run
 * must not replay already-performed acts.
 */
export function beatKey(b: Beat): string {
  return b.key ?? `${b.seq}:${b.kind}:${b.lane}:${b.atMs}`
}

/** Bench-slot assignment helper — first-seen order, stable across rerenders. */
export function agentsInOrder(events: TraceEvent[]): string[] {
  const seen = new Set<string>()
  const order: string[] = []
  const visit = (list: TraceEvent[]): void => {
    for (const e of list) {
      if (e.kind === 'subagent_fanout') {
        for (const a of e.agents as SubagentRun[]) {
          if (!seen.has(a.agentId)) {
            seen.add(a.agentId)
            order.push(a.agentId)
          }
        }
        continue
      }
      if (e.kind === 'subagent_started' && !seen.has(e.meta.agentId)) {
        seen.add(e.meta.agentId)
        order.push(e.meta.agentId)
      }
    }
  }
  visit(events)
  return order
}
